import {
  type CheckoutError,
  CheckoutErrorType,
  CheckoutIntegrationErrorCode,
  CheckoutRequestErrorCode,
  CheckoutSubmitErrorCode,
  PaymentDeclineReason,
} from '@checkout.com/checkout-web-components'
import { type ValueOf } from '@dni-types/utilities/value-of'

export const CKO_PAYMENT_ERROR_KIND = {
  DECLINED: 'declined',
  INVALID_CARD_INPUT: 'invalid_card_input',
  SESSION_EXPIRED: 'session_expired',
  CONFIGURATION: 'configuration',
  RETRYABLE: 'retryable',
  UNKNOWN: 'unknown',
} as const
export type CkoPaymentErrorKind = ValueOf<typeof CKO_PAYMENT_ERROR_KIND>

export interface CkoPaymentErrorInfo {
  code: string
  type: CheckoutError['type']
  /** payment_request_declined 時 details.requestErrorCodes[0] 的細部拒付原因 */
  declineReason?: string
  kind: CkoPaymentErrorKind
}

export const isCkoCheckoutError = (error: unknown): error is CheckoutError =>
  typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'CheckoutError'

const DECLINE_REASON_KIND: Partial<Record<PaymentDeclineReason, CkoPaymentErrorKind>> = {
  [PaymentDeclineReason.InvalidPaymentSessionData]: CKO_PAYMENT_ERROR_KIND.SESSION_EXPIRED,
  [PaymentDeclineReason.PaymentExpired]: CKO_PAYMENT_ERROR_KIND.SESSION_EXPIRED,
  [PaymentDeclineReason.CustomerMisconfiguration]: CKO_PAYMENT_ERROR_KIND.CONFIGURATION,
  [PaymentDeclineReason.MerchantMisconfiguration]: CKO_PAYMENT_ERROR_KIND.CONFIGURATION,
  [PaymentDeclineReason.TryAgain]: CKO_PAYMENT_ERROR_KIND.RETRYABLE,
}

// payment_request_declined 另依 declineReason 細分，故不列於此
const ERROR_CODE_KIND: Record<string, CkoPaymentErrorKind> = {
  [CheckoutSubmitErrorCode.ComponentInvalid]: CKO_PAYMENT_ERROR_KIND.INVALID_CARD_INPUT,
  [CheckoutRequestErrorCode.PaymentRequestFailed]: CKO_PAYMENT_ERROR_KIND.RETRYABLE,
  [CheckoutRequestErrorCode.NetworkError]: CKO_PAYMENT_ERROR_KIND.RETRYABLE,
  [CheckoutRequestErrorCode.TokenizeRequestFailed]: CKO_PAYMENT_ERROR_KIND.RETRYABLE,
  [CheckoutIntegrationErrorCode.PaymentSessionInvalid]: CKO_PAYMENT_ERROR_KIND.SESSION_EXPIRED,
  [CheckoutIntegrationErrorCode.PaymentSessionRequired]: CKO_PAYMENT_ERROR_KIND.SESSION_EXPIRED,
}

// 未列舉的 declineReason → DECLINED；未列舉的 code → UNKNOWN（UI 顯示通用結帳失敗訊息）
const classify = (code: string, declineReason?: string): CkoPaymentErrorKind => {
  if (code === CheckoutRequestErrorCode.PaymentRequestDeclined) {
    return DECLINE_REASON_KIND[declineReason as PaymentDeclineReason] ?? CKO_PAYMENT_ERROR_KIND.DECLINED
  }
  return ERROR_CODE_KIND[code] ?? CKO_PAYMENT_ERROR_KIND.UNKNOWN
}

/** 把 CKO CheckoutError 解析成穩定的分類資訊；非 CKO 錯誤回 null。 */
export const getCkoPaymentErrorInfo = (error: unknown): CkoPaymentErrorInfo | null => {
  if (!isCkoCheckoutError(error)) return null
  const declineReason = error.type === CheckoutErrorType.Request ? error.details?.requestErrorCodes?.[0] : undefined
  return {
    code: error.code,
    type: error.type,
    declineReason,
    kind: classify(error.code, declineReason),
  }
}
